import React from "react";
import { Box } from "@mui/material";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ApplicationRoute } from "../constants/ApplicationUrl";
import { routeContainer } from "./styles/styles";
import { AdminPortal, MyAccount } from "../routes/HeaderMenu";
import { Home } from "../routes/HomeScreen";
import ProviderMaterials from "../routes/ProviderMaterial/containers/ProviderMaterials";
import Training from "../routes/Training/containers/Training";
import VideoPlayer from "../routes/Training/components/VideoPlayer";
import ReferralManagement from "../routes/ReferralMgmt/containers/ReferralManagement";
import Faqs from "../routes/Faqs/containers/Faqs";
import CheckEligibility from "../routes/CheckEligibility/containers/CheckEligibility";
import ProviderListing from "../routes/HeaderMenu/containers/ProviderListing";
import Scorecard from "../routes/Scorecard/containers/Scorecard";
import AgencyAccess from "../routes/HeaderMenu/containers/AgencyAccess";
import RequireAuth from "../app/containers/RequireAuth";
import { FeatureId, ROLE_IDS_ALL, ROLE_IDS_PROVIDER_ADMIN_AND_ABOVE, ROLE_IDS_TANGO_ADMIN_ONLY } from "../constants/HomePage";
import ProviderFinder from "../routes/ProviderFinder/containers/ProviderFinder";
import { PriorAuthWorkFlow } from "../routes/PriorAuthWorkFlow";
import AuthorizationForm from "../routes/PriorAuthWorkFlow/containers/AuthorizationForm";
import { ReAuthWorkFlow } from "../routes/ReAuthWorkFlow";
import ReAuthorizationForm from "../routes/ReAuthWorkFlow/containers/ReAuthorizationForm";
import Notification from "../routes/HeaderMenu/containers/Notification";
import { MyAccountModel } from "../models/MyAccount";
import { getUserRole } from "../utils";

export interface PropsFromState {
  loggedInUser: MyAccountModel
}

/**
 * Role indices (from getUserRole / ROLES_STRINGS order):
 *   0 = Tango Admin
 *   1 = Provider Admin
 *   2 = Provider User
 */
interface RoleGuardProps {
  allowedRoles: number[];
  roleId: number | null;
  children: React.ReactNode;
}

const RoleGuard: React.FC<RoleGuardProps> = ({ allowedRoles, roleId, children }) => {
  const location = useLocation();
  if (roleId === null || !allowedRoles.includes(roleId)) {
    return <Navigate to={ApplicationRoute.HOME} state={{ from: location }} replace />;
  }
  return <>{children}</>;
};

const MainContent: React.FC<PropsFromState> = ({ loggedInUser }) => {
  const roleId: number | null = getUserRole(loggedInUser);

  return (
    <Box sx={routeContainer()}>
      <Routes>
        <Route
          path={ApplicationRoute.HOME}
          element={<Home />}
        />
        <Route
          path={ApplicationRoute.ADMIN}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_PROVIDER_ADMIN_AND_ABOVE} roleId={roleId}>
              <AdminPortal />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.AGENCY_ACCESS}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_TANGO_ADMIN_ONLY} roleId={roleId}>
              <AgencyAccess />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.NOTIFICATION}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <Notification />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.TRAINING}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <Training />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.CHECK_ELIGIBILITY}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <CheckEligibility />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.MEMBER_INFO}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <PriorAuthWorkFlow />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.PRIOR_AUTH_WORKFLOW}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <AuthorizationForm />
            </RoleGuard>
          }
        />
        <Route
          path={`${ApplicationRoute.RE_AUTH_MEMBER_INFO}/:referralId/:providerId`}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <ReAuthWorkFlow />
            </RoleGuard>
          }
        />
        <Route
          path={`${ApplicationRoute.CREATE_RE_AUTH}/:referralId/:providerId`}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <ReAuthorizationForm />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.PROVIDER_FINDER}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <ProviderFinder />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.MY_ACCOUNT}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <MyAccount />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.PROVIDER_LISTING}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <ProviderMaterials />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.FAQS}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <Faqs />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.REFERRAL_MANAGEMENT}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <ReferralManagement />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.VIDEO_PLAYER}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <VideoPlayer />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.PROVIDER_LIST}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_TANGO_ADMIN_ONLY} roleId={roleId}>
              <ProviderListing />
            </RoleGuard>
          }
        />
        <Route
          path={ApplicationRoute.SCORECARD}
          element={
            <RoleGuard allowedRoles={ROLE_IDS_ALL} roleId={roleId}>
              <RequireAuth featureId={FeatureId.SCORECARD}>
                <Scorecard />
              </RequireAuth>
            </RoleGuard>
          }
        />
      </Routes>
    </Box>
  );
};

export default MainContent;